import { useEffect, useRef, useState, type ReactNode } from "react";
import type {
  CheckinTrend,
  CheckinTrendPoint,
  CheckinTrendSeries,
} from "@care-by-ella/contracts";
import { fetchCheckinTrend } from "@/lib/checkin-trend-api";
import { UnauthorizedError } from "@/lib/clinician-api";
import { checkinAnchor } from "@/lib/checkin-anchor";
import { CoachGraphs } from "./CoachGraphs";

// TES-818 punkt 2 — F-04 over time, for the coach.
//
// A-01/F-04: the coaching-relevant check-in answers have to be COMPARABLE
// across weeks, "tillsammans med text, datum, källa och original". The
// existing Incheckningar list answers "what did she say last week?"; it
// cannot answer "is this getting better or worse?", which is the question a
// coaching conversation actually starts from.
//
// Three rules this component exists to keep:
//
//  1. THE CURVE IS NOT THE RECORD. Every series carries its table — date,
//     value, source (which form and which published version asked the
//     question) and the original stored answer. A coach must always be able
//     to get from a dot back to what the patient actually submitted, and the
//     table doubles as the screen-reader equivalent of the drawing.
//
//  2. FREE TEXT IS PART OF THE PICTURE.
//     "Är det något du vill att coachen ska veta?" is read in the same
//     column as the curves, in time order —
//     not behind a tab, where it would be read by nobody.
//
//  3. MEDICAL ANSWERS ARE A STATUS, NEVER CONTENT. The server decides that
//     (apps/api/src/clinician/checkin-trend.ts) and sends one `status_only`
//     series with dates and nothing else. Nothing here can widen it: there
//     is no medical content in the payload to render.
//
// Drawn with inline SVG. No chart library — a line, dots and axis labels is
// the whole requirement, and a clinical reading surface is not the place for
// a dependency whose rendering nobody in the team can explain.

const VIEW_W = 640;
const VIEW_H = 168;
const PAD_LEFT = 40;
// Wide enough for the last date label, which is centred on the last point
// and would otherwise be clipped by the viewBox edge.
const PAD_RIGHT = 24;
const PAD_TOP = 14;
const PAD_BOTTOM = 28;
const INNER_W = VIEW_W - PAD_LEFT - PAD_RIGHT;
const INNER_H = VIEW_H - PAD_TOP - PAD_BOTTOM;

const SOURCE_LABELS: Record<string, string> = {
  weekly_checkin: "Veckoincheckning (F-04)",
};

function sourceLabel(p: CheckinTrendPoint): string {
  return `${SOURCE_LABELS[p.templateKey] ?? p.templateKey} v${p.templateVersion}`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function fmtShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE", {
    month: "2-digit",
    day: "2-digit",
  });
}

function fmtNumber(n: number): string {
  return n.toLocaleString("sv-SE", { maximumFractionDigits: 1 });
}

function fmtRaw(raw: unknown): string {
  if (raw === null || raw === undefined) return "—";
  if (typeof raw === "string") return raw;
  return JSON.stringify(raw);
}

function valueText(
  point: CheckinTrendPoint,
  series: CheckinTrendSeries,
): string {
  if (!point.answered) return "Ej besvarad";
  if (series.disclosure === "status_only") return "Besvarad";
  if (point.value !== null) {
    return series.unit
      ? `${fmtNumber(point.value)} ${series.unit}`
      : fmtNumber(point.value);
  }
  return point.display ?? "—";
}

// The axis the line is drawn against. A question with declared bounds (the
// 1-5 scale) always reads against those, so a good week and a bad week are
// the same height every time it is opened. A free numeric (weight) gets its
// bounds from the data — a 0-400 kg axis would flatten every real change to
// a straight line.
function yDomain(series: CheckinTrendSeries): [number, number] {
  if (series.min !== null && series.max !== null && series.max > series.min) {
    return [series.min, series.max];
  }
  const values = series.points
    .map((p) => p.value)
    .filter((v): v is number => v !== null);
  if (values.length === 0) return [0, 1];
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  // A single point, or a run of identical values, would otherwise give a
  // zero-height domain: the line lands on the axis and the card reads empty.
  if (hi === lo) {
    const pad = Math.max(Math.abs(lo) * 0.05, 1);
    return [lo - pad, hi + pad];
  }
  const pad = (hi - lo) * 0.12;
  return [lo - pad, hi + pad];
}

function xAt(index: number, count: number): number {
  // Index spacing, like the patient app's weight curve: check-ins are weekly,
  // and evenly spaced dots stay readable at 30 points. The exact dates are on
  // the axis and in the table, so nothing is lost by not scaling by time.
  if (count <= 1) return PAD_LEFT + INNER_W / 2;
  return PAD_LEFT + (index / (count - 1)) * INNER_W;
}

function chartSummary(series: CheckinTrendSeries): string {
  const answered = series.points.filter((p) => p.value !== null);
  if (answered.length === 0) return `${series.label}: inga värden.`;
  const first = answered[0]!;
  const last = answered[answered.length - 1]!;
  const unit = series.unit ? ` ${series.unit}` : "";
  const span =
    answered.length === 1
      ? `ett värde, ${fmtNumber(first.value!)}${unit} den ${fmtDate(first.submittedAt)}`
      : `${answered.length} värden mellan ${fmtDate(first.submittedAt)} och ${fmtDate(
          last.submittedAt,
        )}, från ${fmtNumber(first.value!)}${unit} till ${fmtNumber(last.value!)}${unit}`;
  return `${series.label}. Linjediagram med ${span}. Alla värden finns i tabellen under diagrammet.`;
}

function TrendChart({ series }: { series: CheckinTrendSeries }) {
  const [lo, hi] = yDomain(series);
  const count = series.points.length;
  const coords = series.points.map((p, i) =>
    p.value === null
      ? null
      : {
          x: xAt(i, count),
          y: PAD_TOP + (1 - (p.value - lo) / (hi - lo)) * INNER_H,
        },
  );

  // Contiguous runs, so a skipped week breaks the line instead of being
  // interpolated across — the gap IS the information.
  const runs: { x: number; y: number }[][] = [];
  for (const c of coords) {
    if (c === null) {
      if (runs[runs.length - 1]?.length) runs.push([]);
      continue;
    }
    if (runs.length === 0) runs.push([]);
    runs[runs.length - 1]!.push(c);
  }

  const ticks = [hi, (hi + lo) / 2, lo];
  // At most four date labels, whatever the point count — 30 stacked dates are
  // unreadable, and one is not an axis.
  const labelStride = Math.max(1, Math.ceil(count / 4));

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      className="w-full h-auto"
      role="img"
      aria-label={chartSummary(series)}
    >
      <g className="text-surfaceMuted" stroke="currentColor" strokeWidth={1}>
        {ticks.map((t, i) => {
          const y = PAD_TOP + (i / (ticks.length - 1)) * INNER_H;
          return (
            <line key={t} x1={PAD_LEFT} y1={y} x2={VIEW_W - PAD_RIGHT} y2={y} />
          );
        })}
      </g>
      <g className="text-textSecondary" fill="currentColor" fontSize={11}>
        {ticks.map((t, i) => (
          <text
            key={t}
            x={PAD_LEFT - 6}
            y={PAD_TOP + (i / (ticks.length - 1)) * INNER_H + 4}
            textAnchor="end"
          >
            {fmtNumber(t)}
          </text>
        ))}
        {series.points.map((p, i) =>
          i % labelStride === 0 || i === count - 1 ? (
            <text
              key={p.submissionId}
              x={xAt(i, count)}
              y={VIEW_H - 8}
              textAnchor="middle"
            >
              {fmtShortDate(p.submittedAt)}
            </text>
          ) : null,
        )}
      </g>
      <g
        className="text-brand"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        {runs.map((run, i) =>
          run.length > 1 ? (
            <polyline
              key={i}
              points={run
                .map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`)
                .join(" ")}
            />
          ) : null,
        )}
      </g>
      <g className="text-brand" fill="currentColor">
        {series.points.map((p, i) => {
          const c = coords[i];
          if (!c) return null;
          // A single check-in must still read as a chart, not an empty box:
          // the dot is centred and drawn larger so it is unmistakably a value.
          return (
            <circle
              key={p.submissionId}
              cx={c.x}
              cy={c.y}
              r={count === 1 ? 6 : 4}
            >
              <title>{`${fmtDate(p.submittedAt)}: ${valueText(p, series)}`}</title>
            </circle>
          );
        })}
      </g>
    </svg>
  );
}

// The textual equivalent of the curve, and the only place the ORIGINAL answer
// is readable. Always rendered — a screen-reader user must not have to find
// and open a disclosure to get the same content a sighted reader gets.
function PointTable({
  series,
  linkToCheckin = false,
}: {
  series: CheckinTrendSeries;
  linkToCheckin?: boolean;
}) {
  const showOriginal = series.disclosure === "full";
  return (
    <div className="mt-sm max-h-48 overflow-y-auto">
      <table className="w-full text-xs">
        <caption className="sr-only">
          {`${series.label} — alla mätpunkter med datum, värde och källa.`}
        </caption>
        <thead className="text-textSecondary">
          <tr className="text-left">
            <th scope="col" className="font-normal py-[2px] pr-sm">
              Datum
            </th>
            <th scope="col" className="font-normal py-[2px] pr-sm">
              Svar
            </th>
            <th scope="col" className="font-normal py-[2px] pr-sm">
              Källa
            </th>
            {showOriginal ? (
              <th scope="col" className="font-normal py-[2px] pr-sm">
                Original
              </th>
            ) : null}
            {linkToCheckin ? (
              <th scope="col" className="font-normal py-[2px]">
                Coachsvar
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {[...series.points].reverse().map((p) => (
            <tr key={p.submissionId} className="border-t border-surfaceMuted">
              <th
                scope="row"
                className="font-normal text-textSecondary py-[3px] pr-sm whitespace-nowrap"
              >
                {fmtDate(p.submittedAt)}
              </th>
              <td className="py-[3px] pr-sm text-textPrimary">
                {valueText(p, series)}
              </td>
              <td className="py-[3px] pr-sm text-textSecondary whitespace-nowrap">
                {/* TES-1074 — the source opens the check-in it came from. */}
                {linkToCheckin ? (
                  <a
                    href={`#${checkinAnchor(p.submissionId)}`}
                    className="text-brand hover:underline"
                  >
                    {sourceLabel(p)}
                  </a>
                ) : (
                  sourceLabel(p)
                )}
              </td>
              {showOriginal ? (
                <td className="py-[3px] pr-sm text-textSecondary">
                  <code className="break-all">
                    {p.answered ? fmtRaw(p.raw) : "—"}
                  </code>
                </td>
              ) : null}
              {/* TES-1210 — A-01: each point shows "kopplad
                  coachfritext/återkoppling när sådan finns". The link opens
                  the check-in row, which shows the reply that was sent. */}
              {linkToCheckin ? (
                <td className="py-[3px] text-textSecondary whitespace-nowrap">
                  {p.coachReplyAt ? (
                    <a
                      href={`#${checkinAnchor(p.submissionId)}`}
                      className="text-brand hover:underline"
                    >
                      Svar skickat {fmtDate(p.coachReplyAt)}
                    </a>
                  ) : (
                    "—"
                  )}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// TES-1210 — A-01: each point shows "kopplad coachfritext/återkoppling när
// sådan finns", so every card's table links the point to its check-in and
// the reply that was sent.
function SeriesCard({
  series,
  children,
}: {
  series: CheckinTrendSeries;
  children?: ReactNode;
}) {
  return (
    <article className="rounded-sm border border-surfaceMuted p-sm">
      <h3 className="text-sm font-medium text-textPrimary">{series.label}</h3>
      {children}
      <PointTable series={series} linkToCheckin />
    </article>
  );
}

// Free text, newest first, with its date and source next to it. Same column
// as the curves: the sentence "sov dåligt hela veckan" is what explains the
// dip above it.
function NoteTimeline({ series }: { series: CheckinTrendSeries }) {
  const written = [...series.points].reverse().filter((p) => p.answered);
  return (
    <article className="rounded-sm border border-surfaceMuted p-sm">
      <h3 className="text-sm font-medium text-textPrimary mb-xs">
        {series.label}
      </h3>
      <ol className="flex flex-col gap-xs">
        {written.map((p) => (
          <li key={p.submissionId} className="border-l-2 border-brand/40 pl-sm">
            <p className="text-xs text-textSecondary">
              {fmtDate(p.submittedAt)} • {sourceLabel(p)}
            </p>
            <p className="text-sm text-textPrimary whitespace-pre-wrap">
              {p.display}
            </p>
          </li>
        ))}
      </ol>
    </article>
  );
}

export function WeeklyCheckinTrends({
  patientId,
  onUnauthorized,
}: {
  patientId: string;
  onUnauthorized?: () => void;
}) {
  const [trend, setTrend] = useState<CheckinTrend | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Held in a ref, not in the effect's dependencies. The mounting page passes
  // this inline, so a new function identity every render would re-run the
  // effect every render — a refetch loop on a patient-near endpoint that also
  // writes an access-log row per call.
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  useEffect(() => {
    let cancelled = false;
    setTrend(null);
    setError(null);
    fetchCheckinTrend(patientId)
      .then((t) => {
        if (!cancelled) setTrend(t);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof UnauthorizedError) {
          onUnauthorizedRef.current?.();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  // TES-1074 point 5 — weight and wellbeing first, each on its own, over the
  // last twelve weeks (CoachGraphs, from the shared series since TES-1482;
  // the server leaves both out of this trend); the stack below holds
  // everything else.
  const series = trend?.series ?? [];
  const plottable = series.filter(
    (s) =>
      s.disclosure === "full" && (s.kind === "scale" || s.kind === "numeric"),
  );
  const notes = series.filter(
    (s) => s.disclosure === "full" && s.kind === "text",
  );
  const choices = series.filter(
    (s) => s.disclosure === "full" && s.kind === "choice",
  );
  const withheld = series.filter((s) => s.disclosure === "status_only");

  return (
    <section className="bg-surface border border-surfaceMuted rounded-md p-md">
      <h2 className="font-display text-lg mb-xs">Incheckningar över tid</h2>
      <p className="text-xs text-textSecondary mb-md">
        Veckoincheckningens svar jämförda vecka för vecka. Varje mätpunkt visar
        datum, källa och det inlämnade originalsvaret.
      </p>

      <div className="mb-sm">
        <CoachGraphs patientId={patientId} onUnauthorized={onUnauthorized} />
      </div>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-sm text-sm">
          Kunde inte hämta trenden: {error}
        </div>
      ) : trend === null ? (
        <p className="text-textSecondary text-sm">Hämtar incheckningar…</p>
      ) : trend.series.length === 0 ? (
        <p className="text-textSecondary text-sm">
          Inga incheckningar att jämföra ännu. Kurvan börjar vid första
          inlämnade veckoincheckningen.
        </p>
      ) : (
        <div className="flex flex-col gap-sm">
          {series.length > 0 ? (
            <h3 className="mt-sm text-sm font-semibold">
              Övriga frågor över tid
            </h3>
          ) : null}
          {plottable.map((s) => (
            <SeriesCard key={s.key} series={s}>
              <TrendChart series={s} />
            </SeriesCard>
          ))}
          {notes.map((s) => (
            <NoteTimeline key={s.key} series={s} />
          ))}
          {choices.map((s) => (
            <SeriesCard key={s.key} series={s} />
          ))}
          {withheld.map((s) => (
            <article
              key={s.key}
              className="rounded-sm border border-surfaceMuted bg-surfaceMuted/30 p-sm"
            >
              <h3 className="text-sm font-medium text-textPrimary">
                {s.label}
              </h3>
              {/* Precise on purpose: these check-ins are the coach's — the
                  escalated ones are not in this payload at all. The medical
                  answers belong to the medical assessment, so saying
                  "ligger hos läkaren" here would imply an escalation that did not
                  happen. */}
              <p className="text-xs text-textSecondary mt-[2px]">
                Besvarade vid varje incheckning. Medicinska frågor jämförs inte
                här — de hör till den medicinska bedömningen. Du ser att de är
                gjorda, inte vad de innehåller.
              </p>
              <PointTable series={s} />
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
